/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
} from '@angular/core';
import {MenuPositionX, MenuPositionY} from './menu-positions';
import {throwMdMenuInvalidPositionX, throwMdMenuInvalidPositionY} from './menu-errors';
import {MdMenuItem} from './menu-item';
import {FocusKeyManager} from '../core/a11y/focus-key-manager';
import {MdMenuPanel} from './menu-panel';
import {Subscription} from 'rxjs/Subscription';
import {transformMenu, fadeInItems} from './menu-animations';
import {ESCAPE} from '../core/keyboard/keycodes';
export class MdMenu implements AfterContentInit, MdMenuPanel, OnDestroy {
private _keyManager: FocusKeyManager;
private _xPosition: MenuPositionX = 'after';
private _yPosition: MenuPositionY = 'below';
/**
 * Subscription to tab events on the menu panel
 */
private _tabSubscription: Subscription;
/**
 * Config object to be passed into the menu's ngClass
 */
_classList: any = {};
/**
 * Position of the menu in the X axis.
 * @return {?}
 */
get xPosition() { return this._xPosition; }
/**
 * @param {?} value
 * @return {?}
 */
set xPosition(value: MenuPositionX) {
    if (value !== 'before' && value !== 'after') {
      throwMdMenuInvalidPositionX();
    }
    this._xPosition = value;
    this.setPositionClasses();
  }
/**
 * Position of the menu in the Y axis.
 * @return {?}
 */
get yPosition() { return this._yPosition; }
/**
 * @param {?} value
 * @return {?}
 */
set yPosition(value: MenuPositionY) {
    if (value !== 'above' && value !== 'below') {
      throwMdMenuInvalidPositionY();
    }
    this._yPosition = value;
    this.setPositionClasses();
  }

   templateRef: TemplateRef<any>;
/**
 * List of the items inside of a menu.
 */
items: QueryList<MdMenuItem>;
/**
 * Whether the menu should overlap its trigger.
 */
overlapTrigger = true;
/**
 * This method takes classes set on the host md-menu element and applies them on the
 * menu template that displays in the overlay container.  Otherwise, it's difficult
 * to style the containing menu from outside the component.
 * @param {?} classes list of class names
 * @return {?}
 */
set classList(classes: string) {
    if (classes && classes.length) {
      this._classList = classes.split(' ').reduce((obj: any, className: string) => {
        obj[className] = true;
        return obj;
      }, {});

      this._elementRef.nativeElement.className = '';
      this.setPositionClasses();
    }
  }
/**
 * Event emitted when the menu is closed.
 */
close = new EventEmitter<void>();
/**
 * @param {?} _elementRef
 */
constructor(private _elementRef: ElementRef) { }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._keyManager = new FocusKeyManager(this.items).withWrap();
    this._tabSubscription = this._keyManager.tabOut.subscribe(() => this._emitCloseEvent());
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this._tabSubscription) {
      this._tabSubscription.unsubscribe();
    }
  }
/**
 * Handle a keyboard event from the menu, delegating to the appropriate action.
 * @param {?} event
 * @return {?}
 */
_handleKeydown(event: KeyboardEvent) {
    switch (event.keyCode) {
      case ESCAPE:
        this._emitCloseEvent();
        return;
      default:
        this._keyManager.onKeydown(event);
    }
  }
/**
 * Focus the first item in the menu. This method is used by the menu trigger
 * to focus the first item when the menu is opened by the ENTER key.
 * @return {?}
 */
focusFirstItem() {
    this._keyManager.setFirstItemActive();
  }
/**
 * This emits a close event to which the trigger is subscribed. When emitted, the
 * trigger will close the menu.
 * @return {?}
 */
_emitCloseEvent(): void {
    this.close.emit();
  }
/**
 * It's necessary to set position-based classes to ensure the menu panel animation
 * folds out from the correct direction.
 * @param {?=} posX
 * @param {?=} posY
 * @return {?}
 */
setPositionClasses(posX = this.xPosition, posY = this.yPosition): void {
    this._classList['mat-menu-before'] = posX === 'before';
    this._classList['mat-menu-after'] = posX === 'after';
    this._classList['mat-menu-above'] = posY === 'above';
    this._classList['mat-menu-below'] = posY === 'below';
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-menu, mat-menu',
  host: {'role': 'menu'},
  templateUrl: 'menu.html',
  styleUrls: ['menu.css'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    transformMenu,
    fadeInItems
  ],
  exportAs: 'mdMenu'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'xPosition': [{ type: Input },],
'yPosition': [{ type: Input },],
'templateRef': [{ type: ViewChild, args: [TemplateRef, ] },],
'items': [{ type: ContentChildren, args: [MdMenuItem, ] },],
'overlapTrigger': [{ type: Input },],
'classList': [{ type: Input, args: ['class', ] },],
'close': [{ type: Output },],
};
}

function MdMenu_tsickle_Closure_declarations() {
/** @type {?} */
MdMenu.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMenu.ctorParameters;
/** @type {?} */
MdMenu.propDecorators;
/** @type {?} */
MdMenu.prototype._keyManager;
/** @type {?} */
MdMenu.prototype._xPosition;
/** @type {?} */
MdMenu.prototype._yPosition;
/**
 * Subscription to tab events on the menu panel
 * @type {?}
 */
MdMenu.prototype._tabSubscription;
/**
 * Config object to be passed into the menu's ngClass
 * @type {?}
 */
MdMenu.prototype._classList;
/** @type {?} */
MdMenu.prototype.templateRef;
/**
 * List of the items inside of a menu.
 * @type {?}
 */
MdMenu.prototype.items;
/**
 * Whether the menu should overlap its trigger.
 * @type {?}
 */
MdMenu.prototype.overlapTrigger;
/**
 * Event emitted when the menu is closed.
 * @type {?}
 */
MdMenu.prototype.close;
/** @type {?} */
MdMenu.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
